/**
 * ColumnsTab Component
 *
 * Provides drag-and-drop interface for configuring pivot table columns:
 * - Search/filter available columns
 * - Add calculated columns
 * - Drag columns to Row Groups (for grouping)
 * - Drag columns to Values (for aggregation)
 *
 * Uses @dnd-kit for drag-and-drop functionality.
 */

import { useState, useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  closestCorners,
} from '@dnd-kit/core';
import type { TableFieldApiResponse, LinkedTableValueApi, LinkedTableSourceApi } from '@loader/shared-types';
import { qualifyFieldId, parseFieldId } from '@loader/shared-types';
import { Input, Checkbox, Button } from '@loader/ui';
import { Search, GripVertical, ChevronDown, ChevronRight, FunctionSquare } from 'lucide-react';

import { DraggableColumnItem } from './columnsTab/DraggableColumnItem';
import { RowGroupsDropZone } from './columnsTab/RowGroupsDropZone';
import { ValuesDropZone } from './columnsTab/ValuesDropZone';

import { CALCULATED_COLUMNS_TABLE_ALIAS } from '@/lib/constants';
import { FormulaBuilderDialog } from '@/features/linkedTables/editLinkedTable/components/FormulaBuilderDialog';
import { createPseudoFieldFromCalculatedColumn, findFieldById } from '@/features/linkedTables/editLinkedTable/utils/viewHelpers';
import type { LinkedTableCalculatedColumn, LinkedTableRelationship, LinkedTableVariable } from '@/lib/types';

type SortOrder = 'asc' | 'desc' | 'none';

/** DnD ID prefixes to avoid collisions between zones */
const ROWGROUP_PREFIX = 'rowgroup-';
const VALUE_PREFIX = 'value-';

/** Get the qualified key for a field (module-level pure function) */
const getFieldKey = (field: TableFieldApiResponse): string => {
  const alias = field.table_alias;
  return alias ? qualifyFieldId(alias, field.id) : field.id;
};

/** Row group sort configuration */
interface RowGroupSort {
  field_id: string;
  direction: 'asc' | 'desc';
}

/**
 * Props for ColumnsTab component
 */
interface ColumnsTabProps {
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
  /** All available fields (including relationship fields) */
  availableFields: TableFieldApiResponse[];
  /** Tables in the linked table (for grouping columns) */
  tables: LinkedTableSourceApi[];
  /** Array of field IDs in row groups (order matters) */
  row_groups: string[];
  /** Array of row group sort configurations */
  row_group_sorts: RowGroupSort[];
  /** Array of value configurations (field_id + aggregation function) */
  values: LinkedTableValueApi[];
  /** Calculated columns (for formula dialog) */
  calculated_columns?: LinkedTableCalculatedColumn[];
  /** Relationships (for formula dialog) */
  relationships?: LinkedTableRelationship[];
  /** Variables available for formula autocomplete */
  variables?: LinkedTableVariable[];
  /** Handler to add a field to row groups */
  onAddToRowGroups?: (fieldId: string) => void;
  /** Handler to remove a field from row groups */
  onRemoveFromRowGroups?: (fieldId: string) => void;
  /** Handler to reorder row groups */
  onReorderRowGroups?: (fieldIds: string[]) => void;
  /** Handler to update sort order for a row group field */
  onUpdateRowGroupSort?: (fieldId: string, direction: SortOrder) => void;
  /** Handler to add a field to values */
  onAddToValues?: (fieldId: string, func: LinkedTableValueApi['function']) => void;
  /** Handler to remove a field from values */
  onRemoveFromValues?: (fieldId: string) => void;
  /** Handler to reorder values */
  onReorderValues?: (values: LinkedTableValueApi[]) => void;
  /** Handler to update aggregation function for a value */
  onUpdateValueFunction?: (fieldId: string, func: LinkedTableValueApi['function']) => void;
  /** Handler to add a calculated column */
  onAddCalculatedColumn?: (calculatedColumn: LinkedTableCalculatedColumn) => void;
  /** Handler to update a calculated column */
  onUpdateCalculatedColumn?: (calculatedColumn: LinkedTableCalculatedColumn) => void;
  /** Handler to remove a calculated column */
  onRemoveCalculatedColumn?: (calculatedColumn: LinkedTableCalculatedColumn) => void;
}

/**
 * ColumnsTab Component
 *
 * Main component for drag-and-drop column configuration
 */
export function ColumnsTab({
  canEdit = true,
  availableFields,
  tables,
  row_groups,
  row_group_sorts,
  values,
  calculated_columns = [],
  relationships = [],
  variables = [],
  onAddToRowGroups,
  onRemoveFromRowGroups,
  onReorderRowGroups,
  onUpdateRowGroupSort,
  onAddToValues,
  onRemoveFromValues,
  onReorderValues,
  onUpdateValueFunction,
  onAddCalculatedColumn,
  onUpdateCalculatedColumn,
  onRemoveCalculatedColumn,
}: ColumnsTabProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  // Track collapsed table groups (collapsed by default for non-primary tables)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // State for editing calculated columns
  const [editingCalcColumn, setEditingCalcColumn] = useState<LinkedTableCalculatedColumn | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Convert row_group_sorts array to Record for easy lookup
  const sortOrders = useMemo(() => {
    const orders: Record<string, SortOrder> = {};
    for (const sort of row_group_sorts) {
      orders[sort.field_id] = sort.direction;
    }
    return orders;
  }, [row_group_sorts]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    })
  );

  // Track which fields are in row groups or values
  const fieldsInUse = useMemo(() => {
    const inRowGroups = new Set(row_groups);
    const inValues = new Set(values.map((v) => v.field_id));
    return { inRowGroups, inValues };
  }, [row_groups, values]);

  // Filter fields based on search query only (show all fields, checked/unchecked based on usage)
  // Exclude fields from "calculated" table_alias since they're shown in the Calculated group
  const filteredFields = useMemo(() => {
    return availableFields.filter((field) => {
      // Exclude calculated columns (they appear in the dedicated Calculated group)
      if (field.table_alias === CALCULATED_COLUMNS_TABLE_ALIAS) {
        return false;
      }

      // Apply search filter only
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return field.name.toLowerCase().includes(query);
      }

      return true;
    });
  }, [availableFields, searchQuery]);

  // Filter calculated columns based on search query
  const filteredCalculatedColumns = useMemo(() => {
    if (!searchQuery.trim()) return calculated_columns;
    const query = searchQuery.toLowerCase();
    return calculated_columns.filter((col) =>
      col.name.toLowerCase().includes(query)
    );
  }, [calculated_columns, searchQuery]);

  // Group filtered fields by table_alias
  const groupedFields = useMemo(() => {
    const groups: Map<string, TableFieldApiResponse[]> = new Map();
    const primaryAlias = tables[0]?.alias || 'primary';

    for (const field of filteredFields) {
      const alias = field.table_alias || primaryAlias;
      if (!groups.has(alias)) {
        groups.set(alias, []);
      }
      groups.get(alias)!.push(field);
    }

    // Sort groups: primary table first, then others alphabetically
    const sortedGroups: Array<{ alias: string; fields: TableFieldApiResponse[]; isCalculated?: boolean }> = [];

    // Add primary table first if it has fields
    if (groups.has(primaryAlias)) {
      sortedGroups.push({ alias: primaryAlias, fields: groups.get(primaryAlias)! });
      groups.delete(primaryAlias);
    }

    // Add other tables sorted alphabetically
    const otherAliases = Array.from(groups.keys()).sort();
    for (const alias of otherAliases) {
      sortedGroups.push({ alias, fields: groups.get(alias)! });
    }

    return sortedGroups;
  }, [filteredFields, tables]);

  // Toggle group collapse
  const toggleGroupCollapse = (alias: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) {
        next.delete(alias);
      } else {
        next.add(alias);
      }
      return next;
    });
  };

  // Handler to toggle field selection (fieldKey is already qualified)
  const handleToggleField = (fieldKey: string, isChecked: boolean) => {
    if (isChecked) {
      // Remove from both row_groups and values
      if (fieldsInUse.inRowGroups.has(fieldKey)) {
        onRemoveFromRowGroups?.(fieldKey);
      }
      if (fieldsInUse.inValues.has(fieldKey)) {
        onRemoveFromValues?.(fieldKey);
      }
    } else {
      // Find the field to check its type (parse qualified ID to get bare ID)
      const parsed = parseFieldId(fieldKey);
      const field = availableFields.find((f) => f.id === parsed.fieldId);
      const dataType = field?.data_type
        ?? calculated_columns.find((c) => c.id === parsed.fieldId)?.data_type;

      // Numeric fields go to values with SUM, others go to row groups
      if (dataType === 'number') {
        onAddToValues?.(fieldKey, 'sum');
      } else {
        onAddToRowGroups?.(fieldKey);
      }
    }
  };

  // Calculate if all filtered fields are in use (for "select all" checkbox)
  const allFieldsChecked = useMemo(() => {
    if (filteredFields.length === 0) return false;
    return filteredFields.every((field) => {
      const key = getFieldKey(field);
      return fieldsInUse.inRowGroups.has(key) || fieldsInUse.inValues.has(key);
    });
  }, [filteredFields, fieldsInUse]);

  // Calculate if some (but not all) filtered fields are in use (for indeterminate state)
  const someFieldsChecked = useMemo(() => {
    if (filteredFields.length === 0) return false;
    const checkedCount = filteredFields.filter((field) => {
      const key = getFieldKey(field);
      return fieldsInUse.inRowGroups.has(key) || fieldsInUse.inValues.has(key);
    }).length;
    return checkedCount > 0 && checkedCount < filteredFields.length;
  }, [filteredFields, fieldsInUse]);

  // Handler to toggle all filtered fields
  const handleToggleAll = () => {
    if (allFieldsChecked) {
      // Deselect all: remove all filtered fields from row_groups and values
      for (const field of filteredFields) {
        const key = getFieldKey(field);
        if (fieldsInUse.inRowGroups.has(key)) {
          onRemoveFromRowGroups?.(key);
        }
        if (fieldsInUse.inValues.has(key)) {
          onRemoveFromValues?.(key);
        }
      }
    } else {
      // Select all: add fields based on their type
      // Numeric fields go to values with SUM, others go to row groups
      for (const field of filteredFields) {
        const key = getFieldKey(field);
        if (!fieldsInUse.inRowGroups.has(key) && !fieldsInUse.inValues.has(key)) {
          if (field.data_type === 'number') {
            onAddToValues?.(key, 'sum');
          } else {
            onAddToRowGroups?.(key);
          }
        }
      }
    }
  };

  // Get the field being dragged (for overlay)
  const activeField = useMemo(() => {
    if (!activeId) return null;

    // Extract actual field ID by removing prefix if present
    let rawId = activeId;
    if (activeId.startsWith(ROWGROUP_PREFIX)) {
      rawId = activeId.slice(ROWGROUP_PREFIX.length);
    } else if (activeId.startsWith(VALUE_PREFIX)) {
      rawId = activeId.slice(VALUE_PREFIX.length);
    }

    return findFieldById(rawId, availableFields, calculated_columns) ?? null;
  }, [activeId, availableFields, calculated_columns]);

  /**
   * Handle drag start
   */
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  /**
   * Handle drag over (for showing drop indicators)
   */
  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id as string | null);
  };

  /**
   * Handle drag end
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeIdRaw = active.id as string;
    const overIdRaw = over.id as string;

    // Extract the actual field ID by removing prefixes if present
    const extractFieldId = (id: string): string => {
      if (id.startsWith(ROWGROUP_PREFIX)) return id.slice(ROWGROUP_PREFIX.length);
      if (id.startsWith(VALUE_PREFIX)) return id.slice(VALUE_PREFIX.length);
      return id;
    };

    // Determine which zone an ID belongs to
    const getZone = (id: string): 'column-list' | 'row-groups' | 'values' | 'zone-container' => {
      if (id === 'row-groups-zone' || id === 'values-zone') return 'zone-container';
      if (id.startsWith(ROWGROUP_PREFIX)) return 'row-groups';
      if (id.startsWith(VALUE_PREFIX)) return 'values';
      return 'column-list';
    };

    const activeFieldId = extractFieldId(activeIdRaw);
    const overFieldId = extractFieldId(overIdRaw);
    const activeZone = getZone(activeIdRaw);
    const overZone = getZone(overIdRaw);

    // Helper to add field to row groups
    const addToRowGroups = () => {
      if (!fieldsInUse.inRowGroups.has(activeFieldId)) {
        // If moving from values zone, remove it from there first
        if (fieldsInUse.inValues.has(activeFieldId)) {
          onRemoveFromValues?.(activeFieldId);
        }
        onAddToRowGroups?.(activeFieldId);
      }
    };

    // Helper to add field to values
    const addToValues = () => {
      if (!fieldsInUse.inValues.has(activeFieldId)) {
        // If moving from row groups zone, remove it from there first
        if (fieldsInUse.inRowGroups.has(activeFieldId)) {
          onRemoveFromRowGroups?.(activeFieldId);
        }
        // Default to 'sum' for numeric fields, 'count' for others
        const parsedActive = parseFieldId(activeFieldId);
        const field = availableFields.find((f) => f.id === parsedActive.fieldId);
        const calcCol = calculated_columns.find((c) => c.id === parsedActive.fieldId);
        const dataType = field?.data_type || calcCol?.data_type || 'text';
        const defaultFunc: LinkedTableValueApi['function'] =
          dataType === 'number' ? 'sum' : 'count';
        onAddToValues?.(activeFieldId, defaultFunc);
      }
    };

    // Check if dropping into row groups zone (either on container or on an item)
    if (overIdRaw === 'row-groups-zone' || overZone === 'row-groups') {
      // If reordering within row groups
      if (activeZone === 'row-groups' && overZone === 'row-groups') {
        const oldIndex = row_groups.indexOf(activeFieldId);
        const newIndex = row_groups.indexOf(overFieldId);

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const newOrder = [...row_groups];
          newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, activeFieldId);
          onReorderRowGroups?.(newOrder);
        }
        return;
      }

      // Adding from column list or moving from values zone
      addToRowGroups();
      return;
    }

    // Check if dropping into values zone (either on container or on an item)
    if (overIdRaw === 'values-zone' || overZone === 'values') {
      // If reordering within values
      if (activeZone === 'values' && overZone === 'values') {
        const activeValueIndex = values.findIndex((v) => v.field_id === activeFieldId);
        const overValueIndex = values.findIndex((v) => v.field_id === overFieldId);

        if (activeValueIndex !== -1 && overValueIndex !== -1 && activeValueIndex !== overValueIndex) {
          const newValues = [...values];
          const [removed] = newValues.splice(activeValueIndex, 1);
          newValues.splice(overValueIndex, 0, removed);
          onReorderValues?.(newValues);
        }
        return;
      }

      // Adding from column list or moving from row groups zone
      addToValues();
      return;
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* Column Selection - Hidden for viewers */}
        {canEdit && (
          <>
            {/* Search Input */}
            <div className="sticky top-0 z-10 bg-sidebar px-3 py-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allFieldsChecked}
                  onCheckedChange={handleToggleAll}
                  {...(someFieldsChecked && { 'data-state': 'indeterminate' })}
                />
                <div className="flex-1 flex items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1">
                  <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-0 h-6 px-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Available Columns List */}
            <div className="px-3">
              {filteredFields.length === 0 && filteredCalculatedColumns.length === 0 ? (
                <div className="text-muted-foreground py-4 text-center text-sm">
                  {searchQuery ? 'No columns found' : 'No columns available'}
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Table Groups */}
                  {groupedFields.map((group) => {
                    const isCollapsed = collapsedGroups.has(group.alias);
                    const isPrimary = group.alias === tables[0]?.alias;

                    return (
                      <div key={group.alias}>
                        {/* Table Group Header */}
                        <button
                          type="button"
                          onClick={() => toggleGroupCollapse(group.alias)}
                          className="flex w-full items-center gap-1.5 py-1.5 px-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {isCollapsed ? (
                            <ChevronRight className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                          <span className="truncate">
                            {group.alias}
                            {isPrimary && ' (primary)'}
                          </span>
                        </button>

                        {/* Collapsible Field List */}
                        {!isCollapsed && (
                          <div className="space-y-0 pl-2">
                            {group.fields.map((field) => {
                              const key = getFieldKey(field);
                              const isChecked = fieldsInUse.inRowGroups.has(key) || fieldsInUse.inValues.has(key);
                              return (
                                <DraggableColumnItem
                                  key={key}
                                  field={field}
                                  fieldKey={key}
                                  isChecked={isChecked}
                                  onToggle={handleToggleField}
                                  disabled={isChecked}
                                  canEdit={canEdit}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Calculated Columns Group */}
                  {filteredCalculatedColumns.length > 0 && (
                    <div>
                      {/* Calculated Group Header */}
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapse('__calculated__')}
                        className="flex w-full items-center gap-1.5 py-1.5 px-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {collapsedGroups.has('__calculated__') ? (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                        <FunctionSquare className="h-3.5 w-3.5" />
                        <span className="truncate">Calculated</span>
                      </button>

                      {/* Collapsible Calculated Columns List */}
                      {!collapsedGroups.has('__calculated__') && (
                        <div className="space-y-0 pl-2">
                          {filteredCalculatedColumns.map((calcCol) => {
                            // Check both bare and qualified forms for calculated columns
                            const calcKey = qualifyFieldId(CALCULATED_COLUMNS_TABLE_ALIAS, calcCol.id);
                            const isChecked = fieldsInUse.inRowGroups.has(calcCol.id) || fieldsInUse.inValues.has(calcCol.id)
                              || fieldsInUse.inRowGroups.has(calcKey) || fieldsInUse.inValues.has(calcKey);
                            // Create a pseudo-field for the DraggableColumnItem
                            const pseudoField = createPseudoFieldFromCalculatedColumn(calcCol);
                            return (
                              <DraggableColumnItem
                                key={calcCol.id}
                                field={pseudoField}
                                isChecked={isChecked}
                                onToggle={handleToggleField}
                                disabled={isChecked}
                                canEdit={canEdit}
                                onEdit={onUpdateCalculatedColumn ? (fieldId) => {
                                  const col = calculated_columns.find(c => c.id === fieldId);
                                  if (col) {
                                    setEditingCalcColumn(col);
                                    setEditDialogOpen(true);
                                  }
                                } : undefined}
                                onDuplicate={onAddCalculatedColumn ? (fieldId) => {
                                  const col = calculated_columns.find(c => c.id === fieldId);
                                  if (col) {
                                    onAddCalculatedColumn({
                                      ...col,
                                      id: crypto.randomUUID(),
                                      name: `${col.name} (copy)`,
                                    });
                                  }
                                } : undefined}
                                onDelete={onRemoveCalculatedColumn ? (fieldId) => {
                                  const col = calculated_columns.find(c => c.id === fieldId);
                                  if (col) {
                                    onRemoveCalculatedColumn(col);
                                  }
                                } : undefined}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Add Formula Button */}
            {onAddCalculatedColumn && (
              <div className="flex justify-end px-3 py-2">
                <FormulaBuilderDialog
                  availableColumns={availableFields}
                  existingCalculatedColumns={calculated_columns}
                  table_id={tables[0]?.target_id || ''}
                  primaryTableName={tables[0]?.alias || ''}
                  relationships={relationships}
                  variables={variables}
                  onAdd={(column) => {
                    onAddCalculatedColumn(column);
                    // Auto-add to Values (number) or Row Groups, matching regular column behavior
                    const qualifiedId = qualifyFieldId(CALCULATED_COLUMNS_TABLE_ALIAS, column.id);
                    if (column.data_type === 'number') {
                      onAddToValues?.(qualifiedId, 'sum');
                    } else {
                      onAddToRowGroups?.(qualifiedId);
                    }
                  }}
                  renderTrigger={(onClick) => (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-full"
                      onClick={onClick}
                    >
                      <FunctionSquare className="h-4 w-4" />
                      Add custom field
                    </Button>
                  )}
                />
              </div>
            )}

            {/* Separator */}
            <div className="border-t border-border" />
          </>
        )}

        {/* Row Groups Drop Zone */}
        <div className="px-3 py-2">
          <RowGroupsDropZone
            canEdit={canEdit}
            rowGroups={row_groups}
            availableFields={availableFields}
            calculatedColumns={calculated_columns}
            onRemove={onRemoveFromRowGroups}
            onReorder={onReorderRowGroups}
            sortOrders={sortOrders}
            onUpdateSortOrder={onUpdateRowGroupSort}
            activeId={activeId}
            overId={overId}
          />
        </div>

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Values Drop Zone */}
        <div className="px-3 py-2">
          <ValuesDropZone
            canEdit={canEdit}
            values={values}
            availableFields={availableFields}
            calculatedColumns={calculated_columns}
            onRemove={onRemoveFromValues}
            onReorder={onReorderValues}
            onUpdateFunction={onUpdateValueFunction}
            activeId={activeId}
            overId={overId}
          />
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay dropAnimation={null}>
        {activeField ? (
          <div className="flex items-center gap-2 bg-background border border-border rounded-lg px-2 py-1.5 shadow-lg">
            <Checkbox checked={true} className="pointer-events-none" />
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{activeField.name}</span>
          </div>
        ) : null}
      </DragOverlay>

      {/* Edit Calculated Column Dialog */}
      {editingCalcColumn && onUpdateCalculatedColumn && (
        <FormulaBuilderDialog
          key={`edit-dialog-${editingCalcColumn.id}`}
          availableColumns={availableFields}
          existingCalculatedColumns={calculated_columns.filter(c => c.id !== editingCalcColumn.id)}
          table_id={tables[0]?.target_id || ''}
          primaryTableName={tables[0]?.alias || ''}
          relationships={relationships}
          variables={variables}
          onAdd={() => {}}
          onUpdate={(updatedColumn) => {
            onUpdateCalculatedColumn(updatedColumn);
            setEditingCalcColumn(null);
            setEditDialogOpen(false);
          }}
          editColumn={editingCalcColumn}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditingCalcColumn(null);
          }}
        />
      )}
    </DndContext>
  );
}
